/**
 * AssetPicker — V22.9.5 credit-efficient typeahead.
 *
 * Wraps a Combobox with debounced calls to /api/asset-profiles/picker.
 * For type="vendor" the backend returns a union of asset_profiles + assets + vendors.
 * If no match, we still allow free-text entry via the "Use «..» as new entry" footer.
 *
 * Props:
 *   api          — the api instance from useAuth()
 *   type         — "business_process" | "application" | "it_service" | "facility" | "vendor"
 *   value        — { id, name } | string | null
 *   onChange     — (selection) => void; selection = { id, name, criticality, source } | { id: null, name } for free-text
 *   allowFreeText — bool (default: true for type=="vendor", false otherwise)
 *   placeholder  — string
 *   testIdSuffix — data-testid suffix
 */
import { useEffect, useRef, useState } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "./ui/command";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Check, ChevronsUpDown, Plus, Loader2 } from "lucide-react";

const TYPE_LABEL = {
  business_process: "Business Process",
  application: "Business Application",
  it_service: "IT Service",
  facility: "Facility",
  vendor: "Vendor",
};

export default function AssetPicker({
  api,
  type,
  value,
  onChange,
  allowFreeText,
  placeholder,
  testIdSuffix = "",
}) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef(null);
  const allowFreeTextEffective = allowFreeText ?? type === "vendor";

  // Selected display value (works for both object and string forms)
  const selectedLabel = !value ? "" : typeof value === "string" ? value : value.name || "";

  // Debounced fetcher
  useEffect(() => {
    if (!open) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await api.get("/asset-profiles/picker", { params: { type, q, limit: 50 } });
        setResults(res.data?.results || []);
      } catch (e) {
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 250);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [open, q, type, api]);

  const handleSelect = (item) => {
    onChange?.(item);
    setOpen(false);
  };

  const handleFreeText = () => {
    if (!q.trim()) return;
    onChange?.({ id: null, name: q.trim(), source: "free_text" });
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-full justify-between font-normal"
          data-testid={`asset-picker-${type}${testIdSuffix ? `-${testIdSuffix}` : ""}`}
        >
          <span className={selectedLabel ? "text-slate-900" : "text-slate-400"}>
            {selectedLabel || placeholder || `Select ${TYPE_LABEL[type] || type}…`}
          </span>
          <ChevronsUpDown className="w-3.5 h-3.5 ml-2 text-slate-400" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[360px] p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput
            placeholder={`Search ${TYPE_LABEL[type] || type}…`}
            value={q}
            onValueChange={setQ}
            data-testid={`asset-picker-input-${type}`}
          />
          <CommandList>
            {loading && (
              <div className="flex items-center justify-center py-6 text-xs text-slate-400">
                <Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" /> Loading…
              </div>
            )}
            {!loading && results.length === 0 && (
              <CommandEmpty>
                <div className="py-3 text-center">
                  <p className="text-xs text-slate-500">No matching {TYPE_LABEL[type]?.toLowerCase() || type} found.</p>
                  {allowFreeTextEffective && q.trim() && (
                    <Button size="sm" variant="ghost" className="mt-2 text-xs" onClick={handleFreeText} data-testid={`asset-picker-freetext-${type}`}>
                      <Plus className="w-3 h-3 mr-1" /> Use "{q.trim()}" as new {TYPE_LABEL[type]?.toLowerCase() || type}
                    </Button>
                  )}
                </div>
              </CommandEmpty>
            )}
            {!loading && results.length > 0 && (
              <CommandGroup>
                {results.map((r) => (
                  <CommandItem
                    key={`${r.source}-${r.id || r.name}`}
                    value={r.name}
                    onSelect={() => handleSelect(r)}
                    data-testid={`asset-picker-option-${type}-${(r.id || r.name).toString().slice(0, 8)}`}
                    className="flex items-center justify-between"
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      <Check className={`w-3.5 h-3.5 flex-shrink-0 ${selectedLabel === r.name ? "opacity-100" : "opacity-0"}`} />
                      <span className="truncate">{r.name}</span>
                      <Badge variant="outline" className={`text-[9px] flex-shrink-0 ${r.criticality === "critical" || r.criticality === "tier_1" ? "border-rose-200 text-rose-700 bg-rose-50" : r.criticality === "high" || r.criticality === "tier_2" ? "border-amber-200 text-amber-700 bg-amber-50" : "border-slate-200 text-slate-600 bg-slate-50"}`}>
                        {r.criticality || "—"}
                      </Badge>
                    </div>
                    {r.source && r.source !== "asset_profile" && (
                      <span className="text-[9px] text-slate-400 italic flex-shrink-0">{r.source}</span>
                    )}
                  </CommandItem>
                ))}
                {allowFreeTextEffective && q.trim() && !results.some(r => r.name.toLowerCase() === q.trim().toLowerCase()) && (
                  <CommandItem onSelect={handleFreeText} data-testid={`asset-picker-freetext-${type}`}>
                    <Plus className="w-3.5 h-3.5 mr-2 text-slate-400" />
                    <span className="text-xs text-slate-500">Use "{q.trim()}" as new {TYPE_LABEL[type]?.toLowerCase() || type}</span>
                  </CommandItem>
                )}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
